<template>
  <b-card no-body>
    <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
      <b-card-title class="mb-0">
        종목
      </b-card-title>
      <div class="d-flex align-items-center">
        <b-button
          variant="outline-primary"
          class="mr-1"
          :disabled="loading || syncing"
          @click="fetchList"
        >
          목록조회
        </b-button>
        <b-button
          variant="primary"
          :disabled="loading || syncing"
          @click="syncFromKiwoom"
        >
          <b-spinner
            v-if="syncing"
            small
            class="mr-50"
          />
          동기화
        </b-button>
      </div>
    </b-card-header>

    <b-card-body>
      <b-alert
        v-if="errorMessage"
        variant="danger"
        show
      >
        {{ errorMessage }}
      </b-alert>

      <b-alert
        v-if="syncResult"
        variant="success"
        show
      >
        동기화 완료: 요청 {{ syncResult.requestCount }}건, 저장 {{ syncResult.savedCount }}건
      </b-alert>

      <b-input-group class="mb-1">
        <b-input-group-prepend is-text>
          <feather-icon icon="SearchIcon" size="14" />
        </b-input-group-prepend>
        <b-form-input
          v-model="keyword"
          placeholder="코드/종목명/시장명 검색"
          @keyup.enter="search"
        />
        <b-input-group-append>
          <b-button
            variant="outline-secondary"
            :disabled="loading"
            @click="search"
          >
            검색
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <b-table
        :items="items"
        :fields="fields"
        responsive
        small
        show-empty
        empty-text="조회된 종목이 없습니다."
        :busy="loading"
        @row-clicked="openChartModal"
      >
        <template #cell(lastPrice)="row">
          <span>{{ formatPrice(row.item.lastPrice) }}</span>
        </template>
      </b-table>

      <div class="d-flex align-items-center justify-content-between mt-1">
        <div class="d-flex align-items-center">
          <span class="mr-50">페이지당</span>
          <b-form-select
            v-model="size"
            :options="sizeOptions"
            size="sm"
            style="width: 90px;"
          />
          <span class="ml-50">건</span>
        </div>
        <b-pagination
          v-model="page"
          :total-rows="totalElements"
          :per-page="size"
          first-number
          last-number
          align="right"
          size="sm"
          class="mb-0"
        />
      </div>
    </b-card-body>

    <b-modal
      id="stock-item-daily-chart-modal"
      v-model="chartModalVisible"
      size="xl"
      :title="`${currentChartLabel} 차트`"
      hide-footer
    >
      <div class="mb-1 font-weight-bold">
        {{ selectedItem.name }} ({{ selectedItem.code }})
      </div>

      <div class="mb-1 d-flex align-items-center">
        <span class="mr-50">차트구분</span>
        <b-form-select
          v-model="chartType"
          :options="chartTypeOptions"
          size="sm"
          style="width: 140px;"
        />
        <template v-if="chartType === 'M'">
          <span class="ml-1 mr-50">분봉</span>
          <b-form-select
            v-model="minuteScope"
            :options="minuteScopeOptions"
            size="sm"
            style="width: 140px;"
          />
        </template>
      </div>

      <b-alert
        v-if="chartErrorMessage"
        variant="danger"
        show
      >
        {{ chartErrorMessage }}
      </b-alert>

      <div
        v-if="chartLoading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>

      <vue-apex-charts
        v-else
        type="candlestick"
        height="420"
        :options="chartOptions"
        :series="chartSeries"
      />
    </b-modal>
  </b-card>
</template>

<script>
import axios from '@axios'
import VueApexCharts from 'vue-apexcharts'
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BModal,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BPagination,
    BSpinner,
    BTable,
    BModal,
    VueApexCharts,
  },
  data() {
    return {
      loading: false,
      syncing: false,
      page: 1,
      size: 20,
      sizeOptions: [20, 50, 100],
      totalElements: 0,
      keyword: '',
      errorMessage: '',
      chartErrorMessage: '',
      syncResult: null,
      items: [],
      chartLoading: false,
      chartModalVisible: false,
      selectedItem: {
        code: '',
        name: '',
      },
      chartSeries: [{ data: [] }],
      chartType: 'D',
      chartTypeOptions: [
        { value: 'M', text: '분봉' },
        { value: 'D', text: '일봉' },
        { value: 'W', text: '주봉' },
        { value: 'MO', text: '월봉' },
      ],
      minuteScope: '3',
      minuteScopeOptions: [
        { value: '1', text: '1분봉' },
        { value: '3', text: '3분봉' },
        { value: '5', text: '5분봉' },
        { value: '10', text: '10분봉' },
      ],
      chartOptions: {
        chart: {
          type: 'candlestick',
          toolbar: { show: true },
        },
        plotOptions: {
          candlestick: {
            colors: {
              upward: '#ff3b30',
              downward: '#2f80ed',
            },
          },
        },
        xaxis: {
          type: 'category',
        },
        tooltip: {
          x: {
            formatter: value => value || '',
          },
        },
        yaxis: {
          tooltip: { enabled: true },
          labels: {
            formatter: value => Number(value).toLocaleString(),
          },
        },
        noData: {
          text: '차트 데이터가 없습니다.',
        },
      },
      fields: [
        { key: 'mrktTp', label: '시장구분' },
        { key: 'code', label: '종목코드' },
        { key: 'name', label: '종목명' },
        { key: 'marketName', label: '시장명' },
        { key: 'state', label: '종목상태' },
        { key: 'lastPrice', label: '전일종가' },
        { key: 'nxtEnable', label: 'NXT' },
      ],
    }
  },
  computed: {
    currentChartLabel() {
      const found = this.chartTypeOptions.find(option => option.value === this.chartType)
      return found ? found.text : '일봉'
    },
  },
  watch: {
    page() {
      this.fetchList()
    },
    size() {
      this.page = 1
      this.fetchList()
    },
    chartType() {
      if (this.chartModalVisible && this.selectedItem.code) {
        this.fetchChart()
      }
    },
    minuteScope() {
      if (this.chartModalVisible && this.selectedItem.code && this.chartType === 'M') {
        this.fetchChart()
      }
    },
  },
  created() {
    this.fetchList()
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    async fetchList() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await axios.post('/api/kiwoom/stock-item/list', {
          page: this.page,
          size: this.size,
          keyword: this.keyword,
        })
        const data = this.parseApiResponse(response)
        this.items = Array.isArray(data.content) ? data.content : []
        this.totalElements = data.totalElements || 0
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : '종목 목록 조회에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    async syncFromKiwoom() {
      this.syncing = true
      this.errorMessage = ''
      try {
        const response = await axios.post('/api/kiwoom/stock-item/sync')
        this.syncResult = this.parseApiResponse(response)
        this.page = 1
        await this.fetchList()
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : '종목 동기화에 실패했습니다.'
      } finally {
        this.syncing = false
      }
    },
    formatPrice(value) {
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return value || '-'
      return parsed.toLocaleString()
    },
    search() {
      this.page = 1
      this.fetchList()
    },
    toNumber(value) {
      if (value === null || value === undefined) return 0
      const cleaned = String(value).replace(/,/g, '')
      const parsed = Number(cleaned)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    formatCategoryDate(value) {
      const text = String(value || '').trim()
      if (text.length === 14) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)} ${text.slice(8, 10)}:${text.slice(10, 12)}`
      }
      if (text.length >= 8) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`
      }
      return text
    },
    async openChartModal(item) {
      if (!item || !item.code) return

      this.selectedItem = {
        code: item.code,
        name: item.name || '',
      }
      this.chartType = 'D'
      this.minuteScope = '3'
      this.chartModalVisible = true
      await this.fetchChart()
    },
    async fetchChart() {
      if (!this.selectedItem.code) return

      this.chartErrorMessage = ''
      this.chartSeries = [{ data: [] }]
      this.chartLoading = true

      try {
        const response = await axios.post('/api/kiwoom/stock-item/chart/daily', {
          code: this.selectedItem.code,
          chartType: this.chartType,
          ticScope: this.chartType === 'M' ? this.minuteScope : undefined,
          count: 120,
        })
        const data = this.parseApiResponse(response)
        const candles = Array.isArray(data.candles) ? data.candles : []
        const seriesData = candles
          .map(candle => {
            const x = this.formatCategoryDate(candle.dt)
            if (!x) return null
            return {
              x,
              y: [
                this.toNumber(candle.openPrice),
                this.toNumber(candle.highPrice),
                this.toNumber(candle.lowPrice),
                this.toNumber(candle.closePrice),
              ],
            }
          })
          .filter(Boolean)

        this.chartSeries = [{ data: seriesData }]
      } catch (error) {
        this.chartErrorMessage = error && error.message ? error.message : '차트 조회에 실패했습니다.'
      } finally {
        this.chartLoading = false
      }
    },
  },
}
</script>
